/** @-mention typeahead helpers — plan/22 (people + docs). */

export type MentionCandidate = { id: string; label: string };

export type DocMentionKind = "sales_order" | "purchase_order" | "product";

export type DocMentionCandidate = {
  kind: DocMentionKind;
  id: string;
  label: string;
  /** Secondary line in the picker (party / sku). */
  hint?: string;
};

/** Active @query ending at cursor (no spaces in the query). */
export function activeMentionQuery(
  body: string,
  cursor: number,
): { start: number; query: string } | null {
  const head = body.slice(0, Math.max(0, cursor));
  const at = head.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0) {
    const prev = head[at - 1];
    if (prev && !/\s/.test(prev)) return null;
  }
  const query = head.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { start: at, query };
}

export function filterMembersForMention(
  members: MentionCandidate[],
  query: string,
  limit = 8,
): MentionCandidate[] {
  const q = query.trim().toLowerCase();
  const ranked = q
    ? members.filter(
        (m) =>
          m.label.toLowerCase().includes(q) ||
          m.id.toLowerCase().startsWith(q),
      )
    : members;
  return ranked.slice(0, limit);
}

export function filterDocsForMention(
  docs: DocMentionCandidate[],
  query: string,
  limit = 8,
): DocMentionCandidate[] {
  const q = query.trim().toLowerCase();
  const ranked = q
    ? docs.filter(
        (d) =>
          d.label.toLowerCase().includes(q) ||
          (d.hint?.toLowerCase().includes(q) ?? false) ||
          d.id.toLowerCase().startsWith(q),
      )
    : docs;
  return ranked.slice(0, limit);
}

export function insertMentionAt(
  body: string,
  cursor: number,
  mentionStart: number,
  userId: string,
): { body: string; cursor: number; mentionUserId: string } {
  const before = body.slice(0, mentionStart);
  const after = body.slice(cursor);
  const token = `@${userId} `;
  const next = `${before}${token}${after}`;
  return {
    body: next,
    cursor: before.length + token.length,
    mentionUserId: userId,
  };
}

/** Storage token `@doc:<kind>:<uuid>` — plan/22. */
export function docMentionToken(kind: DocMentionKind, id: string): string {
  return `@doc:${kind}:${id}`;
}

export function insertDocMentionAt(
  body: string,
  cursor: number,
  mentionStart: number,
  kind: DocMentionKind,
  id: string,
): { body: string; cursor: number } {
  const before = body.slice(0, mentionStart);
  const after = body.slice(cursor);
  const token = `${docMentionToken(kind, id)} `;
  const next = `${before}${token}${after}`;
  return {
    body: next,
    cursor: before.length + token.length,
  };
}

const UUID_MENTION_RE =
  /@([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/gi;

const DOC_MENTION_RE =
  /@doc:(sales_order|purchase_order|product):([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/gi;

export function parseDocMentionRefs(
  body: string,
): Array<{ kind: DocMentionKind; id: string }> {
  const out: Array<{ kind: DocMentionKind; id: string }> = [];
  const seen = new Set<string>();
  const re = new RegExp(DOC_MENTION_RE.source, "gi");
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) != null) {
    const kind = m[1] as DocMentionKind;
    const id = m[2]!.toLowerCase();
    const key = `${kind}:${id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ kind, id });
  }
  return out;
}

/** Replace @uuid / @doc tokens with display labels (storage unchanged). */
export function formatCommentBodyLabels(
  body: string,
  members: MentionCandidate[],
  docs: DocMentionCandidate[] = [],
): string {
  const byUser = new Map(
    members.map((m) => [m.id.toLowerCase(), m.label] as const),
  );
  const byDoc = new Map<string, string>(
    docs.map((d) => [`${d.kind}:${d.id.toLowerCase()}`, d.label]),
  );
  let out = body.replace(DOC_MENTION_RE, (full, kind: string, id: string) => {
    const label = byDoc.get(`${kind}:${id.toLowerCase()}`);
    return label ? `@${label}` : full;
  });
  out = out.replace(UUID_MENTION_RE, (full, id: string) => {
    const label = byUser.get(id.toLowerCase());
    return label ? `@${label}` : full;
  });
  return out;
}

export type CommentBodySegment =
  | { kind: "text"; value: string }
  | { kind: "mention"; userId: string; label: string }
  | {
      kind: "docMention";
      docKind: DocMentionKind;
      docId: string;
      label: string;
    };

/** Split body into text + people/doc mention segments for chip rendering. */
export function parseCommentBodySegments(
  body: string,
  members: MentionCandidate[],
  docs: DocMentionCandidate[] = [],
): CommentBodySegment[] {
  const byUser = new Map(
    members.map((m) => [m.id.toLowerCase(), m.label] as const),
  );
  const byDoc = new Map<string, string>(
    docs.map((d) => [`${d.kind}:${d.id.toLowerCase()}`, d.label]),
  );

  const hits: Array<{ index: number; length: number; seg: CommentBodySegment }> =
    [];

  const docRe = new RegExp(DOC_MENTION_RE.source, "gi");
  let dm: RegExpExecArray | null;
  while ((dm = docRe.exec(body)) != null) {
    const docKind = dm[1] as DocMentionKind;
    const docId = dm[2]!;
    const label =
      byDoc.get(`${docKind}:${docId.toLowerCase()}`) ??
      `${docKind} ${docId.slice(0, 8)}`;
    hits.push({
      index: dm.index,
      length: dm[0].length,
      seg: { kind: "docMention", docKind, docId, label },
    });
  }

  const userRe = new RegExp(UUID_MENTION_RE.source, "gi");
  let um: RegExpExecArray | null;
  while ((um = userRe.exec(body)) != null) {
    // Skip if this @uuid sits inside an already-matched @doc:…:uuid
    const insideDoc = hits.some(
      (h) => um!.index >= h.index && um!.index < h.index + h.length,
    );
    if (insideDoc) continue;
    const userId = um[1]!;
    const label = byUser.get(userId.toLowerCase()) ?? userId.slice(0, 8);
    hits.push({
      index: um.index,
      length: um[0].length,
      seg: { kind: "mention", userId, label },
    });
  }

  hits.sort((a, b) => a.index - b.index);

  const segments: CommentBodySegment[] = [];
  let last = 0;
  for (const h of hits) {
    if (h.index < last) continue;
    if (h.index > last) {
      segments.push({ kind: "text", value: body.slice(last, h.index) });
    }
    segments.push(h.seg);
    last = h.index + h.length;
  }
  if (last < body.length) {
    segments.push({ kind: "text", value: body.slice(last) });
  }
  return segments.length ? segments : [{ kind: "text", value: body }];
}

export function docMentionPath(
  companySlug: string,
  kind: DocMentionKind,
  id: string,
): string {
  switch (kind) {
    case "sales_order":
      return `/c/${companySlug}/sales/orders/${id}`;
    case "purchase_order":
      return `/c/${companySlug}/purchasing/orders/${id}`;
    case "product":
      return `/c/${companySlug}/products/${id}`;
  }
}

export function docMentionKindLabel(kind: DocMentionKind): string {
  switch (kind) {
    case "sales_order":
      return "Sales order";
    case "purchase_order":
      return "Purchase order";
    case "product":
      return "Product";
  }
}
